import { autoinject } from 'aurelia-framework';
import { ApiService } from '../../utils/api-service';
import { SearchFormatter } from '../../utils/search-formatter';
import { AppSettings } from '../../utils/appsettings';
import { every, get } from 'lodash';

export interface IAutocompleteProvider {
	getItems(searchTerm: string): Promise<AutocompleteItem[]>;

	renderName(name: string, specifier: string): string;
}

export interface IAutocompleteProviderSettings {
	idKey: string;
	nameKey: string;
	specifierKey: string;
	urlKey: string;
	translateName: boolean;
}

export interface IAutocompleteOptions {
	dataSource: string | any[];
	settings: IAutocompleteProviderSettings;
}

abstract class AutocompleteProviderBase implements IAutocompleteProvider {

	constructor(protected settings: IAutocompleteProviderSettings) { }

	abstract getItems(searchTerm: string): Promise<AutocompleteItem[]>;


	protected processItems(searchTerm: string, items: any[]): AutocompleteItem[] {
		return items.map(m => {
			const name = get(m, this.settings.nameKey + (this.settings.translateName ? `.${AppSettings.lang}` : '')) as string;
			const specifier = get(m, this.settings.specifierKey) as string;
			const url = get(m, this.settings.urlKey) as string;
			return new AutocompleteItem(
				get(m, this.settings.idKey) as string,
				name,
				specifier,
				url,
				this.displayFormat(name, specifier, searchTerm));
		});
	}

	protected displayFormat(name: string, specifier: string, pattern: string) {
		const display = this.renderName(name, specifier);
		return SearchFormatter.formatSearch(display, pattern);
	}

	public renderName(name: string, specifier: string) {
		return name + (specifier ? ` (${specifier})` : '');
	}
}

class ArrayAutocompleteProvider extends AutocompleteProviderBase {
	constructor(private data: any[], settings: IAutocompleteProviderSettings) {
		super(settings);
	}

	async getItems(searchTerm: string): Promise<AutocompleteItem[]> {
		return this.processItems(searchTerm, this.performSearch(searchTerm));
	}

	private performSearch(searchTerm: string): AutocompleteItem[] {

		const searchTermsLower = searchTerm.split(/\s+/).map(t => t.toLowerCase());
		const searchTermRegexes = searchTermsLower.map(s => new RegExp(s, 'i'));

		const matches = this.data.map(item => {
			const name = get(item, this.settings.nameKey) as string;
			const specifier = get(item, this.settings.specifierKey) as string;

			return every(searchTermRegexes, t => t.test(name) || t.test(specifier))
				? { item: item, startsWithFirstSearchTerm: name.toLowerCase().startsWith(searchTermsLower[0]) } : null;
		}).filter(m => m !== null);

		// Sort those first that begin with the first search term
		return matches.sort((a, b) =>
			a.startsWithFirstSearchTerm === b.startsWithFirstSearchTerm ? 0
				: a.startsWithFirstSearchTerm ? -1 : 1)
			.map(m => m.item);
	}
}

class ApiAutocompleteProvider extends AutocompleteProviderBase {

	constructor(
		private api: ApiService,
		private apiUrl: string,
		settings: IAutocompleteProviderSettings) {
		super(settings);
	}

	getItems(searchTerm: string): Promise<AutocompleteItem[]> {
		const separator = this.apiUrl.indexOf('?') === -1 ? '?' : '&';
		return this.api.fetchJson(`${this.apiUrl}${separator}searchTerm=${encodeURIComponent(searchTerm)}`)
			.then(resources => this.processItems(searchTerm, resources));
	}
}

@autoinject
export class AutocompleteProviderFactory {
	constructor(private api: ApiService) { }

	public getProvider(opts: IAutocompleteOptions): IAutocompleteProvider {
		if (typeof opts.dataSource === "string") {
			return new ApiAutocompleteProvider(this.api, opts.dataSource, opts.settings);
		}

		return new ArrayAutocompleteProvider(opts.dataSource || [], opts.settings);
	}
}

export class AutocompleteItem {
	isActive = false;

	constructor(
		public id: string,
		public name: string,
		public specifier: string,
		public url: string,
		public display: string) {
	}
}
